import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  Heading,
  ModalOverlay,
  Modal as RACModal,
} from "react-aria-components";
import { cn } from "../../../lib";
import Button from "../../atoms/Button/Button";

const modalVariants = cva("modal !bottom-0 !transform-none", {
  variants: {
    color: {
      default: "border border-lf-blue-dark text-lf-blue-dark bg-white",
    },
    size: {
      auto: "w-auto",
      medium: "w-[37rem]",
    },
    className: {
      default:
        "bg-white shadow-lg rounded-lg p-5 max-w-[95%] max-h-[95%] overflow-auto",
    },
  },
  defaultVariants: {
    color: "default",
    className: "default",
    size: "medium",
  },
});

interface ModalProps extends VariantProps<typeof modalVariants> {
  data: {
    title: string;
    subtitle: string;
    closeButtonText?: string;
    confirmButtonText?: string;
  };
  size?: "medium" | "auto";
  closeModal?: () => void;
  confirmChanges?: (() => void) | null;
  isConfirmingChanges?: boolean;
  isOpen?: boolean;
  isDismissable?: boolean;
  className?: string;
  selectedItems?: number;
  children?: React.ReactNode;
  testId?: string;
}

const useKeyboardAvoidance = () => {
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [translateYValue, setTranslateYValue] = useState("0");

  useEffect(() => {
    const handleFocusIn = () => {
      setKeyboardVisible(true);
      setTranslateYValue("calc(-30%)");
    };
    const handleFocusOut = () => {
      setKeyboardVisible(false);
      setTranslateYValue("0");
    };

    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("focusin", handleFocusIn);
    window.addEventListener("focusout", handleFocusOut);
    window.addEventListener("resize", checkIsMobile);

    checkIsMobile();

    return () => {
      window.removeEventListener("focusin", handleFocusIn);
      window.removeEventListener("focusout", handleFocusOut);
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  return { keyboardVisible, translateYValue, isMobile };
};

const Modal = (props: ModalProps) => {
  const {
    data: {
      title,
      subtitle,
      closeButtonText = "Cancel",
      confirmButtonText = "Confirm",
    },
    isOpen = false,
    isDismissable = false,
    className,
    closeModal,
    confirmChanges,
    isConfirmingChanges = false,
    color,
    size,
    children,
    testId,
  } = props;

  const { keyboardVisible, translateYValue, isMobile } = useKeyboardAvoidance();
  const racModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (racModalRef.current) {
      if (isMobile) {
        racModalRef.current.style.transform = keyboardVisible
          ? `translateY(${translateYValue})`
          : "translateY(0)";
      } else {
        racModalRef.current.style.transform = "none";
      }
    }
  }, [keyboardVisible, translateYValue, isMobile]);

  return (
    <ModalOverlay
      className={`fixed top-0 bottom-0 left-0 w-screen z-50 flex items-center justify-center bg-black/50 ${!/(android)/i.test(navigator.userAgent) ? "h-[var(--visual-viewport-height)]" : ""}`}
      isOpen={isOpen}
      isDismissable={isDismissable}
    >
      <RACModal
        className={cn(modalVariants({ color, size }), className)}
        ref={racModalRef}
      >
        <Dialog>
          <Heading
            className="text-lg md:text-2xl text-lf-blue-dark font-bold pb-5"
            slot="title"
          >
            {title}
          </Heading>
          <p className="uppercase text-base md:text-lg text-lf-blue-dark font-bold">
            {subtitle}
          </p>

          {children && (
            <div className="text-sm text-black block my-2">{children}</div>
          )}
          <div className="flex justify-end mt-4 space-x-3">
            <Button
              variant={"outline"}
              onClick={closeModal}
              disabled={isConfirmingChanges}
              className={isConfirmingChanges ? "border-lf-blue-dark" : ""}
              data-testid={testId ? `${testId}-close-btn` : ""}
            >
              {isConfirmingChanges ? (
                <span className="inline-block w-6 h-6 border-3 border-gray-300 border-b-lf-blue-dark rounded-full animate-spin"></span>
              ) : (
                closeButtonText
              )}
            </Button>
            {confirmChanges && (
              <Button
                variant={"default"}
                onClick={confirmChanges}
                data-testid={testId ? `${testId}-confirm-btn` : ""}
              >
                {confirmButtonText}
              </Button>
            )}
          </div>
        </Dialog>
      </RACModal>
    </ModalOverlay>
  );
};

Modal.displayName = "Modal";

export default Modal;
export { modalVariants };
export type { ModalProps };
